import { BookmarkIcon } from '@heroicons/react/solid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Divider,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/joy';
import dayjs from 'dayjs';
import { ContentState, convertFromRaw, EditorState } from 'draft-js';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import EmployeeIcon from '../../../assets/icon.png';
import EditorModal from '../../../components/postModal';
import { useUser } from '../../../hooks/useUser';
import { deletePost } from '../../../redux/actions/postActions';
import { setPinnedActivity } from '../../../redux/services/pinnedAcitvities';
import JoinEventButton from '../../joinEventButton';
import AttachedItems from '../activityCard/AttachedItems';
import Comment from '../comment/Comment';
import CommentForm from '../comment/CommentForm';
import Metadata from './metaData';

type TPostProps = {
  post: {
    _id: string;
    body: string;
    comments: any[];
    createdAt: string;
    event: {
      _id: string;
      title: string;
      imageUrl: string;
      dateStart: string;
      timeStart: string;
    };
    fileUrls: string[];
    likedBy: any[];
    owner?: {
      _id: string;
      firstName: string;
      lastName: string;
      imageUrl: string;
      personType: string;
    };
    pinned: boolean;
    topic: {
      _id: string;
      title: string;
    };
    type: string;
    responseToCommentId: string;
  };
  refetch: () => void;
  hideQuickNavigate: boolean;
};

const createEditorStateFromPostBody = (postBodySavedFormat: string) => {
  try {
    const rawState = convertFromRaw(JSON.parse(postBodySavedFormat));
    const parsedState = EditorState.createWithContent(rawState);
    return parsedState;
  } catch (error) {
    // Fallback: return the original post body
    console.error(error);
    const content = ContentState.createFromText(postBodySavedFormat);
    const stateWithRawBody = EditorState.createWithContent(content);
    return stateWithRawBody;
  }
};

function Post(props: Readonly<TPostProps>) {
  const { post, refetch, hideQuickNavigate } = props;
  const [showAllComments, setShowAllComments] = useState(false);
  const [responseToCommentId, setResponseToCommentId] = useState<null | string>(
    null
  );
  const [editPostModalOpen, setEditPostModalOpen] = useState(false);
  const dispatch = useDispatch();
  const userQuery = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [editorState, setEditorState] = useState(() =>
    createEditorStateFromPostBody(props.post.body)
  );

  // Add this useEffect
  React.useEffect(() => {
    setEditorState(createEditorStateFromPostBody(post.body));
  }, [post.body]); // Update when post.body changes

  const handleSelectAction = async (
    actionName: 'aanpassen' | 'verwijderen' | 'pin'
  ) => {
    if (actionName === 'aanpassen') {
      setEditPostModalOpen(true);
    }
    if (actionName === 'verwijderen') {
      dispatch(deletePost(post._id));
      await refetch();
    }
    if (actionName === 'pin') {
      await setPinnedActivity(post._id, !post.pinned);
      await refetch();
    }
  };

  const user = userQuery.data;

  const comments = useMemo(() => {
    const orderedComments = post.comments.sort((item1: any, item2: any) => {
      const date1 = new Date(item1.createdAt);
      const date2 = new Date(item2.createdAt);

      if (date1 > date2) return 1;
      if (date1 < date2) return -1;
      return 0;
    });
    return orderedComments;
  }, [post.comments, showAllComments]);

  const responseToCommentLabel: string | null = useMemo(() => {
    if (!responseToCommentId) {
      return null;
    }
    const label = post.comments.find((comment) => {
      return comment._id === responseToCommentId;
    })?.owner.firstName;
    return label;
  }, [responseToCommentId]);

  // If a comment is a response to another comment, its index should be after the comment it is responding to
  const groupedComments = comments.reduce((acc, comment) => {
    if (comment.responseToCommentId) {
      const parentCommentIndex = acc.findIndex(
        (c: any) => c._id === comment.responseToCommentId
      );
      if (parentCommentIndex !== -1) {
        acc.splice(parentCommentIndex + 1, 0, comment);
      } else {
        acc.push(comment);
      }
    } else {
      acc.push(comment);
    }
    return acc;
  }, []) as any[];

  const slicedComments = showAllComments
    ? groupedComments
    : groupedComments.slice(0, 3);

  if (!userQuery.isSuccess || !post) {
    return null;
  }

  return (
    <>
      <EditorModal
        postId={post._id}
        setOpen={() => {
          setEditPostModalOpen(false);
        }}
        isOpen={editPostModalOpen}
        onPostSubmit={async () => {
          await refetch();
        }}
        asModal={true}
      />
      <Card key={post._id}>
        <div className="flex flex-col space-y-5">
          {/* User, tag, date */}
          <div className="flex justify-between px-2 pt-2">
            <div className="flex space-x-3">
              <Link to={`/profile/${post.owner?._id}`}>
                <Avatar src={post.owner?.imageUrl} alt="profile" />
              </Link>

              <Stack>
                <Link to={`/profile/${post.owner?._id}`}>
                  <Stack direction={'row'} gap={0.5}>
                    <Typography level="body-md">
                      {post.owner?.firstName} {post.owner?.lastName}
                    </Typography>

                    {/* Render icon if user is schoolscool employee */}
                    {post.owner?.personType === 'Employee' ? (
                      <img
                        alt={`Schoolscool medewerker icoon`}
                        src={EmployeeIcon}
                        className="w-5 h-5"
                      />
                    ) : null}
                  </Stack>
                </Link>

                {/* Date */}
                <Typography level="body-sm">
                  {dayjs(post?.createdAt).fromNow()}
                </Typography>
              </Stack>
            </div>

            <div className="flex space-x-1">
              {post.pinned && (
                <p>
                  <BookmarkIcon className="w-8 text-primary" />
                </p>
              )}
              {user?._id === post.owner?._id ||
              user?.role === 'ADMIN' ||
              user?.role === 'SUPER_ADMIN' ? (
                // Joy UI's <Dropdown> wraps <MenuButton> + <Menu> and wires
                // up outside-click + escape-key close automatically. The
                // bare <Menu> we used previously stayed open when the user
                // clicked elsewhere on the page — pre-existing UX bug.
                <Dropdown>
                  <MenuButton
                    slots={{ root: IconButton }}
                    slotProps={{ root: { variant: 'plain', color: 'neutral' } }}
                    aria-label="post actions"
                  >
                    <MoreVertIcon />
                  </MenuButton>
                  <Menu placement="bottom-end">
                    {['aanpassen', 'verwijderen', 'pin'].map((option) => (
                      <MenuItem
                        key={option}
                        onClick={() => handleSelectAction(option as any)}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Menu>
                </Dropdown>
              ) : null}
            </div>
          </div>

          {!hideQuickNavigate && (
            <Stack
              alignItems={'center'}
              justifyContent={'start'}
              direction={'row'}
              sx={{ padding: '0 0.8rem' }}
              spacing={1}
            >
              <Typography>Post in:</Typography>
              {/* Link to event */}
              {post.event && (
                <Chip
                  onClick={() => navigate(`/events/${post.event?._id}`)}
                  color="primary"
                >
                  {post.event?.title}
                </Chip>
              )}
              {/* Link to topic */}
              {post.topic && (
                <Chip
                  onClick={() => navigate(`/topics/${post.topic?._id}`)}
                  color="primary"
                >
                  {post.topic?.title}
                </Chip>
              )}
            </Stack>
          )}

          {post.type === 'announcement' ? (
            <div className="flex flex-col space-y-3">
              <div className="px-3">
                {`Neem deel aan het evenement '${post.event?.title}' op ${dayjs(
                  new Date(post.event?.dateStart)
                ).format('D MMMM YYYY, HH:mm')}`}
              </div>
              <Link to={`/events/${post.event?._id}`}>
                {post.event?.imageUrl && (
                  // Only render when imageUrl is set — otherwise the
                  // browser shows the broken-image icon + the alt text
                  // ("Plaatje van het evenement"), which is what surfaced
                  // on staging events that hadn't been uploaded a banner.
                  <div className="flex justify-center my-3 p-3">
                    <img
                      alt="Plaatje van het evenement"
                      className="rounded-md"
                      style={{ maxWidth: '100%', height: 'auto' }}
                      src={post.event.imageUrl}
                    />
                  </div>
                )}
              </Link>
              <section className="flex justify-between space-x-8 px-3">
                <JoinEventButton eventId={post.event?._id} />
              </section>
            </div>
          ) : null}

          {/* Text content of post */}
          {post.type !== 'announcement' && (
            <Editor
              key={post._id}
              readOnly
              toolbarHidden
              editorState={editorState}
              onEditorStateChange={setEditorState}
            />
          )}

          <AttachedItems fileUrls={post.fileUrls} />

          <Stack direction={'row'} spacing={1}>
            {/* Likes, comment, bookmarks */}
            {post.type === 'announcement' ? null : (
              <Metadata
                likes={post.likedBy}
                postId={post._id}
                refetch={refetch}
                key={`${post._id}-comments`}
              />
            )}
          </Stack>

          {/* Divider between post and comments */}
          {comments.length > 0 && <Divider />}

          {/* Comments */}
          <Stack spacing={2}>
            {slicedComments.map((comment, index) => {
              return (
                <>
                  {index > 0 && <Divider />}

                  <Comment
                    onDelete={async () => {
                      await refetch();
                    }}
                    key={comment._id}
                    comment={comment}
                    id={comment._id}
                    onClickReply={setResponseToCommentId}
                    responseToLabel={
                      comment.responseToCommentId
                        ? post.comments.find(
                            (comment2) =>
                              comment2._id === comment.responseToCommentId
                          )?.owner.firstName
                        : null
                    }
                  />
                </>
              );
            })}
          </Stack>

          {/* Show more button */}
          {post.comments.length > 3 && (
            <Button
              startDecorator={<QuestionAnswerRoundedIcon />}
              onClick={() => setShowAllComments((curr) => !curr)}
              variant="plain"
            >
              <p>
                {showAllComments
                  ? t('posts.show_less')
                  : `${t('posts.show_more')} (${post.comments.length})`}
              </p>
            </Button>
          )}
        </div>

        <Divider />

        <CommentForm
          postId={post._id}
          responseToCommentLabel={responseToCommentLabel}
          responseToCommentId={responseToCommentId}
          clearResponseToComment={() => setResponseToCommentId(null)}
        />
      </Card>
    </>
  );
}

export default Post;
